import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Typography, CircularProgress } from "@mui/material";
// The app's real notification system (App.js's global <Toaster>, top-right
// under the nav) — this page's Save/Delete errors were a plain inline MUI
// Alert instead, rendering in the wrong place (Rahim, 2026-09-04, audit:
// "the notifications are not in the correct place... just like app
// notifications are being used"). Same import/call pattern every other
// module (Tasks etc.) already uses.
import { toast } from "react-hot-toast";
import PanelHeading from "../../Navbar/PanelHeading";
// Plain MUI Close (simple "X", no circle) — the established close-button
// icon everywhere else in the app (see KnowledgeSourcesTab.jsx's own
// delete-confirm dialog, built the same session this one is modeled on).
import CloseIcon from "@mui/icons-material/Close";
// Iconsax, matching the LeftNav rail's icon set (see icon-library-iconsax
// memory). DirectInbox added when the "Inbox AI" tab was re-enabled
// (2026-09-05) — old MUI InboxIcon dropped, tab.Icon now expects the same
// iconsax component shape as the other two tabs.
import { Book1 as KnowledgeTabIconIx, AddCircle as NewAgentIconIx, ArrowLeft2 as BackIconIx, DirectInbox as InboxAiTabIconIx } from "iconsax-react";
import { useSelector } from "react-redux";
import { getAuthToken } from "../../../utils/getAuthToken";

import {
  SERVER_DOMAIN,
  EMPTY_AGENT,
  EMPTY_ACTION_CONFIGS,
  DEFAULT_SCHEDULE,
  EMPTY_OFFICE_HOURS,
  EMPTY_CONTACT_FILTER,
  EMPTY_RESPONSE_PREFERENCES,
} from "./AiSettings/constants";
import AgentsTab, { UsageMeter, CreditsBalance } from "./AiSettings/tabs/AgentsTab";
import KnowledgeSourcesTab from "./AiSettings/tabs/KnowledgeSourcesTab";
import InboxAiTab from "./AiSettings/tabs/InboxAiTab";
import AgentConfigView from "./AiSettings/components/AgentConfigView";

const NAV_TABS = [
  // Doubles as the real "create agent" trigger (Rahim, 2026-09-01) — clicking
  // it both switches to the Agents list AND opens the creation wizard, so
  // finishing/cancelling lands back on the list where the new agent shows up.
  // Replaces the old separate green "New Agent" button in AgentsTab.jsx's
  // own header (removed, redundant with this).
  { label: "Create New Agent",  Icon: NewAgentIconIx },
  { label: "Knowledge Sources", Icon: KnowledgeTabIconIx },
  // Re-enabled (Rahim, 2026-09-05) — was hidden since 2026-09-01 pending the
  // other tabs; InboxAiTab.jsx + its content branch below were untouched
  // the whole time, just unreachable without this entry.
  { label: "Inbox AI", Icon: InboxAiTabIconIx },
];

// Same button styling as the "Assign to AI"/"Learn More" button
// (ChatboxNavbar.jsx) — reused here for the tab-as-button treatment, per
// [[ai-settings-page-rebuild-plan]] (style off Inbox's own conventions).
// Active state used to fill with primary.main (green) — Rahim (2026-09-01):
// "never ever do a green button". Now a neutral dark/light-inverted fill
// instead (text.primary bg / background.paper text) — still a clear
// "selected pill" look, just not brand-colored.
const tabButtonSx = (active) => ({
  borderRadius: "8px",
  backgroundColor: (theme) => (active ? theme.palette.text.primary : theme.palette.background.default),
  borderColor: (theme) => (active ? theme.palette.text.primary : theme.palette.divider),
  "&:hover": {
    backgroundColor: (theme) => (active ? theme.palette.text.primary : theme.palette.action.hover),
    borderColor: (theme) => (active ? theme.palette.text.primary : theme.palette.divider),
    opacity: active ? 0.9 : 1,
  },
  color: (theme) => (active ? theme.palette.background.paper : theme.palette.text.primary),
  textTransform: "none",
  fontWeight: 500,
  // whiteSpace/flexShrink (Rahim, 2026-09-02 responsive fix) — without
  // these, a tight flex row let the label itself wrap to multiple lines
  // inside a squished button instead of the row wrapping the buttons.
  whiteSpace: "nowrap",
  flexShrink: 0,
  px: { xs: 1, sm: 1.375, md: 1.375, lg: 1.375, xl: 1.375 },
  py: { xs: 0.5, sm: 0.625, md: 0.625, lg: 0.625, xl: 0.625 },
  fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.8125rem", lg: "0.8125rem", xl: "0.8125rem" },
});

// Explicit exception to this page's "never ever do a green button" rule
// (Rahim, 2026-09-05: "make create new agent button green") — every other
// button on this page stays neutral, this one's the one deliberate brand-
// colored call-to-action.
const createAgentButtonSx = {
  borderRadius: "8px",
  backgroundColor: (theme) => theme.palette.primary.main,
  borderColor: (theme) => theme.palette.primary.main,
  "&:hover": {
    backgroundColor: (theme) => theme.palette.primary.dark,
    borderColor: (theme) => theme.palette.primary.dark,
  },
  color: (theme) => theme.palette.primary.contrastText,
  textTransform: "none",
  fontWeight: 500,
  whiteSpace: "nowrap",
  flexShrink: 0,
  px: { xs: 1, sm: 1.375, md: 1.375, lg: 1.375, xl: 1.375 },
  py: { xs: 0.5, sm: 0.625, md: 0.625, lg: 0.625, xl: 0.625 },
  fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.8125rem", lg: "0.8125rem", xl: "0.8125rem" },
};

// Same neutral bordered formula as the list page's own "Learn More" button
// below, extracted so the new Cancel/Save Agent buttons in the Create/Edit
// header match it exactly (Rahim, 2026-09-04: "add the save agent and
// cancel agent button on the top nav right side").
const PANEL_HEADER_BUTTON_SX = {
  borderRadius: "8px",
  backgroundColor: (theme) => theme.palette.background.default,
  borderColor: (theme) => theme.palette.divider,
  "&:hover": {
    backgroundColor: (theme) => theme.palette.action.hover,
    borderColor: (theme) => theme.palette.divider,
  },
  color: (theme) => theme.palette.text.primary,
  textTransform: "none",
  fontWeight: 500,
  px: { xs: 1, sm: 1.375, md: 1.375, lg: 1.375, xl: 1.375 },
  py: { xs: 0.5, sm: 0.625, md: 0.625, lg: 0.625, xl: 0.625 },
  fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.8125rem", lg: "0.8125rem", xl: "0.8125rem" },
};

function AiSettings() {
  const currentWorkspace = useSelector((state) => state.workspace?.currentWorkspace);
  const workspaceId = currentWorkspace?._id;

  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState("list");

  const [agents, setAgents] = useState([]);
  const [workflowCounts, setWorkflowCounts] = useState({});
  const [credits, setCredits] = useState({ remaining: 0 });
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [editingAgentId, setEditingAgentId] = useState(null);
  const [form, setForm] = useState(EMPTY_AGENT);
  const [intentDraft, setIntentDraft] = useState({ name: "", keywords: "", entityKey: "" });
  const [saving, setSaving] = useState(false);

  const [sources, setSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);

  const loadAgents = useCallback(async () => {
    if (!workspaceId) return;
    setAgentsLoading(true);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/agents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAgents(Array.isArray(data?.agents) ? data.agents : []);
      setWorkflowCounts(data?.workflowCounts || {});
      setCredits(data?.credits || { remaining: 0 });
    } catch (err) { console.error("Failed to load agents", err); }
    finally { setAgentsLoading(false); }
  }, [workspaceId]);

  const loadSources = useCallback(async () => {
    if (!workspaceId) return;
    setSourcesLoading(true);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/knowledge-sources`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSources(Array.isArray(data?.sources) ? data.sources : []);
    } catch (err) { console.error("Failed to load knowledge sources", err); }
    finally { setSourcesLoading(false); }
  }, [workspaceId]);

  useEffect(() => { loadAgents(); }, [loadAgents]);
  useEffect(() => { loadSources(); }, [loadSources]);

  const openCreatePage = () => {
    setEditingAgentId(null);
    setForm({ ...EMPTY_AGENT, actionConfigs: { ...EMPTY_ACTION_CONFIGS } });
    setIntentDraft({ name: "", keywords: "", entityKey: "" });
    setViewMode("config");
  };

  const openEditPage = (agent) => {
    setEditingAgentId(agent._id);
    setForm({
      name: agent.name || "",
      instructions: { ...EMPTY_AGENT.instructions, ...(agent.instructions || {}) },
      intents: Array.isArray(agent.intents) ? agent.intents : [],
      knowledgeSourceIds: Array.isArray(agent.knowledgeSourceIds)
        ? agent.knowledgeSourceIds.map((id) => String(id))
        : [],
      fallbackMessage: agent.fallbackMessage || EMPTY_AGENT.fallbackMessage,
      enabledActions: Array.isArray(agent.enabledActions) ? agent.enabledActions : [],
      actionConfigs: { ...EMPTY_ACTION_CONFIGS, ...(agent.actionConfigs || {}) },
      officeHours: agent.officeHours
        ? {
            enabled: !!agent.officeHours.enabled,
            timezone: agent.officeHours.timezone || "UTC",
            schedule: Array.isArray(agent.officeHours.schedule) && agent.officeHours.schedule.length
              ? agent.officeHours.schedule
              : DEFAULT_SCHEDULE.map((d) => ({ ...d })),
          }
        : { ...EMPTY_OFFICE_HOURS, schedule: DEFAULT_SCHEDULE.map((d) => ({ ...d })) },
      contactFilter: agent.contactFilter
        ? {
            filterType: agent.contactFilter.filterType || "all",
            specificContacts: Array.isArray(agent.contactFilter.specificContacts)
              ? agent.contactFilter.specificContacts.map((c) => ({
                  id: String(c.id), name: c.name || "", phone: c.phone || "",
                }))
              : [],
            countryCodes: Array.isArray(agent.contactFilter.countryCodes) ? agent.contactFilter.countryCodes : [],
            labels: Array.isArray(agent.contactFilter.labels) ? agent.contactFilter.labels : [],
          }
        : { ...EMPTY_CONTACT_FILTER },
      useStructuredPlanner: agent.useStructuredPlanner !== false,
      responsePreferences: { ...EMPTY_RESPONSE_PREFERENCES, ...(agent.responsePreferences || {}) },
      isDefault: !!agent.isDefault,
      isActive: agent.isActive !== false,
    });
    setIntentDraft({ name: "", keywords: "", entityKey: "" });
    setViewMode("config");
  };

  const addIntent = () => {
    if (!intentDraft.name.trim()) return;
    const keywords = intentDraft.keywords.split(",").map((k) => k.trim()).filter(Boolean);
    const entityKey = intentDraft.entityKey.trim() || null;
    setForm((prev) => ({
      ...prev,
      intents: [...prev.intents, { name: intentDraft.name.trim(), keywords, entityKey }],
    }));
    setIntentDraft({ name: "", keywords: "", entityKey: "" });
  };

  const removeIntent = (i) =>
    setForm((prev) => ({ ...prev, intents: prev.intents.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Agent name is required"); return; }
    setSaving(true);
    try {
      const token = await getAuthToken();
      const url = editingAgentId
        ? `${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/agents/${editingAgentId}`
        : `${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/agents`;
      const res = await fetch(url, {
        method: editingAgentId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data?.message || "Failed to save agent"); return; }
      setViewMode("list");
      loadAgents();
    } catch { toast.error("Failed to save agent"); }
    finally { setSaving(false); }
  };

  // Was a bare window.confirm() (the raw browser "localhost:3000 says"
  // dialog, Rahim 2026-09-04: "when deleting an agent it uses default...
  // fix it" — same complaint already fixed for Knowledge Sources' delete
  // this session). Split into requestDeleteAgent (opens the styled dialog
  // below, needs the whole agent for its name) + handleDelete (the actual
  // DELETE call, now fired from that dialog's own Delete button).
  const [deleteAgentTarget, setDeleteAgentTarget] = useState(null);
  const [deletingAgent, setDeletingAgent] = useState(false);
  const requestDeleteAgent = (agent) => setDeleteAgentTarget(agent);
  const closeDeleteAgentDialog = () => setDeleteAgentTarget(null);

  const handleDelete = async (agentId) => {
    setDeletingAgent(true);
    try {
      const token = await getAuthToken();
      await fetch(`${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/agents/${agentId}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteAgentTarget(null);
      loadAgents();
    } catch (err) { console.error("Failed to delete agent", err); }
    finally { setDeletingAgent(false); }
  };

  // Card-level Active/Inactive toggle (Rahim, 2026-09-01) — reuses the same
  // PUT .../ai/agents/:id endpoint the Edit wizard's Save button already
  // calls. Confirmed safe via server/controllers/aiAgentController.js:
  // updateAgent only sets fields that are !== undefined, so sending just
  // { isActive } here is a genuine partial update — every other field on
  // the agent is left untouched. Optimistic local update + rollback on
  // failure so the switch feels instant.
  const handleToggleActive = async (agentId, nextActive) => {
    const prevAgents = agents;
    setAgents((prev) => prev.map((a) => (a._id === agentId ? { ...a, isActive: nextActive } : a)));
    try {
      const token = await getAuthToken();
      const res = await fetch(`${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/agents/${agentId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ isActive: nextActive }),
      });
      if (!res.ok) throw new Error("Failed to update agent status");
    } catch (err) {
      console.error("Failed to toggle agent active state", err);
      setAgents(prevAgents);
    }
  };

  if (viewMode === "config") {
    return (
      // height:"100%" not "100vh" — this is nested inside AiSettingsPage.jsx's
      // own bounded flex chain (flex:1, minHeight:0), and 100vh here double-
      // counts the TopNav-height space that's already accounted for higher up
      // (the exact "100vh inside an already-bounded flex parent" gotcha
      // documented in ui-gotchas memory). Fixed while restructuring this
      // block to also carry the shared PanelHeading + back button.
      <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <PanelHeading
          // Was hardcoded "AI Agents" regardless of create vs edit (Rahim,
          // 2026-09-02: "when clicked for the create ai agent update the
          // heading in the top bar to create ai agent") — now reflects
          // which flow is actually open, same editingAgentId flag
          // AgentConfigView.jsx already uses for its own inner heading.
          title={editingAgentId ? "Edit AI Agent" : "Create AI Agent"}
          variant="full"
          leading={
            <IconButton size="small" onClick={() => setViewMode("list")} sx={{ color: (theme) => theme.palette.text.primary }}>
              <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <BackIconIx size="100%" color="currentColor" variant="Linear" />
              </Box>
            </IconButton>
          }
          // Save Agent + Cancel (Rahim, 2026-09-04: "go for it for the edit
          // and create new agent") — moved up from AgentConfigView.jsx's
          // footer into this header's own empty `action` slot (the exact
          // mechanism the plain AI Agents list page already uses for its
          // own "Learn More" button, just below). This header is visible
          // on every step already, same as the footer was, so nothing about
          // "save from any step" is lost — and it's one less row of chrome
          // competing with the actual form content for space. Cancel does
          // the same thing the back arrow already does (no confirmation
          // dialog) — just a second, more explicit way to reach it.
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setViewMode("list")}
                sx={PANEL_HEADER_BUTTON_SX}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSave}
                disabled={saving}
                sx={PANEL_HEADER_BUTTON_SX}
              >
                {saving ? "Saving…" : "Save Agent"}
              </Button>
            </Box>
          }
        />
        {/* pt:0, pb:0 (was p:3 uniform) — AgentConfigView.jsx's Test Agent
            zone needs to touch this box's top AND bottom edges directly
            (Rahim, 2026-09-03: "make the background container to stretch
            the top" / "the parent background has space in the bottom...
            not sticking below"). AgentConfigView.jsx's left column now
            carries its own pt:3/pb:3 to compensate, so its content still
            sits exactly where it did before. */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", pt: 0, pr: 3, pb: 0, pl: 3, boxSizing: "border-box" }}>
          <AgentConfigView
            form={form} setForm={setForm}
            onBack={() => setViewMode("list")}
            sources={sources} sourcesLoading={sourcesLoading}
            workspaceId={workspaceId} editingAgentId={editingAgentId}
            intentDraft={intentDraft} setIntentDraft={setIntentDraft}
            onAddIntent={addIntent} onRemoveIntent={removeIntent}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", minHeight: "100%" }}>
      {/* Panel heading — replaces the global TopNav's space now that it's
          hidden on /ai routes (Home.jsx's NO_TOP_NAV_ROUTES). Same shared
          component Inbox's panel heading pattern was extracted into
          (components/Navbar/PanelHeading.jsx), just at full page width
          instead of Inbox's narrow panel width. "Learn More" moved here
          from TopNav.jsx — no destination wired yet.
          See [[ai-settings-page-rebuild-plan]]. */}
      <PanelHeading
        // Was hardcoded "AI Agents" regardless of which sub-tab was open —
        // same "reflect what's actually open" fix already applied to the
        // Create/Edit Agent view's heading above (editingAgentId ternary).
        title={activeTab === 1 ? "Knowledge Sources" : activeTab === 2 ? "Inbox AI" : "AI Agents"}
        variant="full"
        leading={
          // Knowledge Sources (and now Inbox AI, restored 2026-09-05 as its
          // own drill-down page the same way) are a step away from the AI
          // home page (Rahim, 2026-09-01) — the New Agent/Knowledge Sources
          // tab row is replaced entirely by a simple back button, same
          // treatment as the Agent config view's own back button (its own
          // early return above).
          (activeTab === 1 || activeTab === 2) ? (
            <IconButton size="small" onClick={() => setActiveTab(0)} sx={{ color: (theme) => theme.palette.text.primary }}>
              <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <BackIconIx size="100%" color="currentColor" variant="Linear" />
              </Box>
            </IconButton>
          ) : null
        }
        action={
          <Button
            variant="outlined"
            size="small"
            // Was a dead button, no onClick at all (Rahim, 2026-09-04: "wire
            // the learn more button ... to clusterden book a demo page").
            // Opens the WhatsApp "book a demo" chat link in a new tab —
            // same target="_blank"+rel="noopener noreferrer" pattern used
            // for external links elsewhere in the app.
            component="a"
            href="https://api.whatsapp.com/send/?phone=923334167724&text&type=phone_number&app_absent=0"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              borderRadius: "8px",
              backgroundColor: (theme) => theme.palette.background.default,
              borderColor: (theme) => theme.palette.divider,
              "&:hover": {
                backgroundColor: (theme) => theme.palette.action.hover,
                borderColor: (theme) => theme.palette.divider,
              },
              color: (theme) => theme.palette.text.primary,
              textTransform: "none",
              fontWeight: 500,
              px: { xs: 1, sm: 1.375, md: 1.375, lg: 1.375, xl: 1.375 },
              py: { xs: 0.5, sm: 0.625, md: 0.625, lg: 0.625, xl: 0.625 },
              fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.8125rem", lg: "0.8125rem", xl: "0.8125rem" },
            }}
          >
            Learn More
          </Button>
        }
      />

      {/* Sub-tabs (the true AI home page) — hidden whenever Knowledge
          Sources or Inbox AI is open, replaced by the back button above
          instead (both are drill-down pages now, not peer tabs). */}
      {activeTab === 0 && (
        <Box
          sx={{
            // Was px:1/pt:1 — sat flush against PanelHeading's bottom border
            // (Rahim, 2026-09-01). Left inset was PanelHeading's own
            // pl:"18px" (lining up with "AI Agents" above), but that sat 6px
            // left of the card grid below (Tab content Box uses p:3=24px) —
            // Rahim (2026-09-02): "make the top buttons a little right to
            // match the card alignment". Now pl:3 (24px), matching the card
            // container instead. Top roomier so the row clears the border.
            // pr was 4 (32px) — 8px more than the Tab-content Box below
            // (p:3=24px), so the Agents/Credits-Balance stats sat closer to
            // center than the card grid's own right edge. Now pr:3 (24px)
            // to match exactly (Rahim, 2026-09-02: "make sure left and right
            // spacing is correct for the whole page").
            pl: 3,
            pr: 3,
            pt: 2,
            pb: { xs: 2, sm: 0 },
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            rowGap: 1.5,
            // Rahim (2026-09-02): "buttons are not responsive" — this row had
            // no wrap at all, so on phone/tablet widths the button labels
            // themselves wrapped to 3 lines inside squished buttons and the
            // Agents/Credits-Balance stats got pushed half off-screen
            // (horizontal scroll). flexWrap lets the stats drop to their own
            // row below the buttons instead of both groups fighting for the
            // same line; the buttons/text themselves stay single-line via
            // whiteSpace:nowrap below rather than compressing.
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {NAV_TABS.map((tab, index) => {
            // "Create New Agent" is a pure action button (opens the wizard
            // immediately), not a real persisted tab state, so it never
            // shows the green "active" fill (Rahim, 2026-09-01) — only
            // Knowledge Sources still toggles.
            const active = index !== 0 && activeTab === index;
            return (
              <Button
                key={tab.label}
                variant="outlined"
                size="small"
                onClick={() => {
                  setActiveTab(index);
                  // The "New Agent" tab-button doubles as the real creation
                  // trigger — opens the wizard directly instead of just
                  // switching views, per Rahim (2026-09-01).
                  if (index === 0) openCreatePage();
                }}
                startIcon={
                  <Box sx={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <tab.Icon size="100%" color="currentColor" variant={active || index === 0 ? "Bold" : "Linear"} />
                  </Box>
                }
                sx={index === 0 ? createAgentButtonSx : tabButtonSx(active)}
              >
                {tab.label}
              </Button>
            );
          })}
          </Box>
          {/* Usage meters — moved here (Rahim, 2026-09-01) so they sit
              truly in-line with the Create New Agent/Knowledge Sources
              buttons instead of on their own row below (was AgentsTab.jsx's
              "Page header" row). See [[ai-settings-page-rebuild-plan]]. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <UsageMeter label="Agents" used={agents.length} limit={10} color="primary" loading={agentsLoading} />
            <CreditsBalance balance={credits.remaining} loading={agentsLoading} />
          </Box>
        </Box>
      )}

      {/* Tab content */}
      <Box sx={{ p: 3 }}>
        {activeTab === 0 && (
          <AgentsTab
            agents={agents}
            loading={agentsLoading}
            workspaceId={workspaceId}
            workflowCounts={workflowCounts}
            onCreateNew={openCreatePage}
            onEdit={openEditPage}
            onDelete={requestDeleteAgent}
            onToggleActive={handleToggleActive}
          />
        )}

        {activeTab === 1 && (
          <KnowledgeSourcesTab
            workspaceId={workspaceId}
            sources={sources}
            loading={sourcesLoading}
            onRefresh={loadSources}
            agents={agents}
          />
        )}

        {activeTab === 2 && <InboxAiTab workspaceId={workspaceId} agents={agents} />}
      </Box>

      {/* Delete confirmation — matches KnowledgeSourcesTab.jsx's own
          delete-confirm dialog exactly (built the same session), which
          itself matches Inbox's MessageCard.jsx confirmDialog: title+close
          row, body copy in an inner Box (MUI's DialogTitle+DialogContent
          padding-collapse fix — see that file for the full writeup),
          Cancel/Delete split via space-between. */}
      <Dialog
        open={!!deleteAgentTarget}
        onClose={() => !deletingAgent && closeDeleteAgentDialog()}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1rem", fontWeight: 600, color: "text.primary", borderBottom: "1px solid", borderColor: "divider", px: 3, py: 2.25 }}>
          Delete AI Agent
          <IconButton size="small" onClick={closeDeleteAgentDialog} disabled={deletingAgent}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 0, pb: 3 }}>
          <Box sx={{ pt: 3 }}>
            <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
              Are you sure you want to delete <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>{deleteAgentTarget?.name}</Box>?
              This can't be undone, and any workflows using it will stop working.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider", justifyContent: "space-between" }}>
          <Button
            onClick={closeDeleteAgentDialog}
            disabled={deletingAgent}
            variant="text"
            sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8125rem", color: (theme) => theme.palette.text.secondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(deleteAgentTarget._id)}
            disabled={deletingAgent}
            color="error"
            variant="contained"
            disableElevation
            autoFocus
            startIcon={deletingAgent ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.8125rem" }}
          >
            {deletingAgent ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AiSettings;
